import React, { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Providers } from 'App/components/providers/Providers';
import { PIXEL_TRACKING_IDENTIFY_PATH } from 'Pages/pixelTrackingIdentify/identify';

import 'App/i18n/config';
import 'bootstrap';
import '@disqotech/vinyl-design-system';
import '@disqotech/vinyl-design-system/consumer.css';
import '@disqotech/vinyl-design-system/globals.css';
import 'App/style/reset.css';
import 'App/style/style.css';
import Router from './router';

const PixelTrackingIdentify = lazy(() => import('Pages/pixelTrackingIdentify/PixelTrackingIdentify'));

//Conditionally enable/start mocking server (must be ready before the app makes its first API call)
const startMockServer = async () => {
  if (process.env.APP_ENV === 'development' && process.env.MOCK_API === 'on') {
    try {
      const { worker } = await import('Mocks/browser');
      await worker.start();
    } catch (error) {
      // Don't block the app when the mock worker fails; API calls will hit the real backend
      // eslint-disable-next-line no-console
      console.error('Mock server failed to start', error);
    }
  }
};

// Register service worker if not in development mode
if (process.env.NODE_ENV !== 'development') {
  if ('serviceWorker' in navigator) {
    const registerServiceWorker = () => {
      navigator.serviceWorker.register('/service-worker.js').then(
        (registration) => {
          // eslint-disable-next-line no-console
          console.log("SW Succeeded:", registration);
        },
        (error) => {
          // eslint-disable-next-line no-console
          console.error(`SW Failed: ${error}`);
        },
      );
    };

    if (document.readyState === 'complete') {
      registerServiceWorker();
    } else {
      window.addEventListener('load', () => {
        registerServiceWorker();
      });
    }
  }
}

const render = () => {
  const container = document.getElementById('app-root') as HTMLElement;
  const root = createRoot(container);

  // /pixel/tracking/identify is an isolated page: it bypasses the Router (auth, refresh, shutdown, maintenance).
  const isPixelTrackingIdentifyPage = window.location.pathname.replace(/\/$/, '') === PIXEL_TRACKING_IDENTIFY_PATH;

  root.render(<Providers>{isPixelTrackingIdentifyPage ? <Suspense fallback={null}><PixelTrackingIdentify /></Suspense> : <Router />}</Providers>);
  window.clearLoadTimeout();
};

const domReady = document.readyState === 'loading'
  ? new Promise<void>((resolve) => window.addEventListener('load', () => resolve(), { once: true }))
  : Promise.resolve();

Promise.all([startMockServer(), domReady]).then(render);